import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import { shakeElement } from 'services/utils/shakeElement';
import { FieldOption } from 'types/FieldTypes';
import Dropdown from 'components/forms/basefields/Dropdown';
import SearchSelect from 'components/forms/basefields/SearchSelect';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import LocationForm from '../../views/contacts/LocationForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/forms.scss';

// One row of the bulk form
interface AssetBulkRow {
    key: string;
    name: string;
    asset_type: number | null;
    number: string;
}

// Type of an asset type option in the type dropdown
interface AssetTypeOption {
    id: number;
    name: string;
    [key: string]: any;
}

// Create an empty row with an optional prefilled name
const createRow = (name = ''): AssetBulkRow => ({ key: uuidv4(), name, asset_type: null, number: '' });

const AssetBulkForm: React.FC<{
    viewKey?: string;
    linkedItem?: { [key: string]: any };
    searchValue?: string;
    onSwitchToSingle: () => void;
}> = ({
    viewKey,
    linkedItem,
    searchValue,
    onSwitchToSingle,
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert, setUnsavedChanges, setPreventClosing } = useGlobalContext();
    const { modalStack, closeModal } = useModal();
    const [rows, setRows] = useState<AssetBulkRow[]>([createRow(searchValue ?? ''), createRow(), createRow()]);
    const [rowErrors, setRowErrors] = useState<Record<string, Record<string, boolean>>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [buttonLoader, setButtonLoader] = useState(false);
    const nameInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const focusRowKey = useRef<string | null>(null);

    // The location is given by the linked item or chosen in the form itself
    const linkedLocationId = linkedItem?.location ? Number(linkedItem.location) : null;
    const [selectedLocation, setSelectedLocation] = useState<FieldOption | null>(null);
    const locationId = linkedLocationId ?? (selectedLocation?.id ? Number(selectedLocation.id) : null);

    // Get the modal this form is rendered in, to call its success callback
    const currentModal = modalStack.find(modal => modal.key === viewKey) ?? modalStack[modalStack.length - 1];

    // Fetch the asset types for the type dropdown of every row
    const { response: assetTypeResponse } = useFetchData({
        apiUrl: 'get_asset_type_list',
        params: useMemo(() => ({ deleted: false }), []),
        useCache: true,
    });
    const assetTypes: AssetTypeOption[] = assetTypeResponse?.results ?? [];

    // Focus the name input of a newly added row
    useEffect(() => {
        if (focusRowKey.current && nameInputRefs.current[focusRowKey.current]) {
            nameInputRefs.current[focusRowKey.current]?.focus();
            focusRowKey.current = null;
        }
    }, [rows]);

    // Mark the modal as having unsaved changes when a row is filled in
    const registerChange = () => {
        if (viewKey) setUnsavedChanges(viewKey, true);
    };

    // Update one field of a row
    const updateRow = (rowKey: string, field: keyof Omit<AssetBulkRow, 'key'>, value: string | number | null) => {
        setRows(previousRows => previousRows.map(row => row.key === rowKey ? { ...row, [field]: value } : row));
        setRowErrors(previousErrors => {
            if (!previousErrors[rowKey]) return previousErrors;
            const { [rowKey]: removed, ...remainingErrors } = previousErrors;
            return remainingErrors;
        });
        registerChange();
    };

    // Add an empty row and focus it
    const addRow = () => {
        const newRow = createRow();
        focusRowKey.current = newRow.key;
        setRows(previousRows => [...previousRows, newRow]);
    };

    // Remove a row, keeping at least one row in the form
    const removeRow = (rowKey: string) => {
        setRows(previousRows => previousRows.length > 1 ? previousRows.filter(row => row.key !== rowKey) : [createRow()]);
        registerChange();
    };

    // Pressing enter in the last row adds a new row, otherwise it moves to the next row
    const handleNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, rowIndex: number) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (rowIndex === rows.length - 1) {
            addRow();
        } else {
            nameInputRefs.current[rows[rowIndex + 1].key]?.focus();
        }
    };

    // Determine whether a row has any input at all
    const isRowFilled = (row: AssetBulkRow) => Boolean(row.name.trim() || row.number.trim() || row.asset_type);

    // Validate the rows and submit them to the bulk endpoint
    const handleSubmit = async () => {

        // Only the rows with any input are submitted
        const filledRows = rows.filter(isRowFilled);

        // Require a location before saving
        if (!locationId) {
            setGeneralError('asset.general.bulk_location_required_error');
            if (currentModal?.modalRef) shakeElement(currentModal.modalRef.current as HTMLElement);
            return;
        }

        // Require at least one filled row
        if (filledRows.length === 0) {
            setGeneralError('asset.general.bulk_no_rows_error');
            if (currentModal?.modalRef) shakeElement(currentModal.modalRef.current as HTMLElement);
            return;
        }

        // Every filled row needs a name
        const missingNameErrors: Record<string, Record<string, boolean>> = {};
        filledRows.forEach(row => {
            if (!row.name.trim()) missingNameErrors[row.key] = { name: true };
        });
        if (Object.keys(missingNameErrors).length > 0) {
            setRowErrors(missingNameErrors);
            setGeneralError('asset.general.bulk_name_required_error');
            if (currentModal?.modalRef) shakeElement(currentModal.modalRef.current as HTMLElement);
            return;
        }

        try {
            setButtonLoader(true);
            setGeneralError(null);

            // Post the rows to the bulk endpoint
            const source = axios.CancelToken.source();
            const response = await saveData({
                apiUrl: 'post_asset_bulk',
                method: 'post',
                data: {
                    location: locationId,
                    ...(linkedItem?.parent_asset ? { parent_asset: linkedItem.parent_asset } : {}),
                    assets: filledRows.map(row => ({
                        name: row.name.trim(),
                        asset_type: row.asset_type,
                        number: row.number.trim(),
                    })),
                },
                source,
            });

            // Handle the successful response
            if (response?.status === 201) {
                setButtonLoader(false);
                setFloatingAlert({ 'type': 'success' });

                // Reset the unsaved changes before closing the modal
                if (viewKey) setUnsavedChanges(viewKey, false);

                // Close the modal before the callback so a follow up modal is not closed right away
                closeModal();

                // Hand the created objects to the opener, for example to select them in a form
                if (currentModal?.props?.onSuccess) currentModal.props.onSuccess(response.data);
            }
        } catch (error: any) {
            setButtonLoader(false);

            // Map the row errors of the backend back to the submitted rows
            const backendRowErrors = error?.response?.data?.assets;
            if (backendRowErrors && typeof backendRowErrors === 'object' && !Array.isArray(backendRowErrors)) {
                const mappedErrors: Record<string, Record<string, boolean>> = {};
                Object.entries(backendRowErrors).forEach(([rowIndex, fieldErrors]) => {
                    const row = filledRows[Number(rowIndex)];
                    if (row) {
                        mappedErrors[row.key] = Object.fromEntries(Object.keys(fieldErrors as object).map(field => [field, true]));
                    }
                });
                setRowErrors(mappedErrors);
            }

            // Show a general error and shake the modal
            setGeneralError('asset.general.bulk_save_error');
            if (currentModal?.modalRef) shakeElement(currentModal.modalRef.current as HTMLElement);
        }
    };

    // Closes the modal and drops the unsaved changes
    const handleClose = () => {
        if (viewKey) setUnsavedChanges(viewKey, false);
        closeModal();
    };

    return (
        <form className='formset add-edit-form asset-bulk-form'
              onSubmit={(event) => { event.preventDefault(); handleSubmit(); }}>
            {generalError && (
                <div className='alert form-alert alert-danger' role='alert'>
                    {t(generalError)}
                </div>
            )}

            {/* Let the user pick the location when it is not given by the opener */}
            {!linkedLocationId && (
                <div className='asset-bulk-location'>
                    <label>{t('asset.general.location_long_label')}</label>
                    <SearchSelect
                        type='searchselect'
                        name='location'
                        value={selectedLocation}
                        placeholder='asset.general.location_placeholder'
                        objectName='location.general.object_name.singular'
                        query={{ endpoint: 'get_location_search' }}
                        selectionFormat='address'
                        optionFormat={{ title: { field: 'address', format: (value) => value } }}
                        onSelect={(option) => { setSelectedLocation(option); setGeneralError(null); registerChange(); }}
                        onUnlink={() => setSelectedLocation(null)}
                        addNewItem={{ method: 'modal-form', modalForm: <LocationForm /> }}
                        onPreventClose={(shouldPreventClose) => { if (viewKey) setPreventClosing(viewKey, shouldPreventClose); }}
                    />
                </div>
            )}

            {/* The column labels of the rows */}
            <div className='asset-bulk-header'>
                <span>{t('asset.general.name_label')}</span>
                <span>{t('asset.general.asset_type_label')}</span>
                <span>{t('asset.general.number_label')}</span>
                <span></span>
            </div>

            {/* One row per object to create */}
            {rows.map((row, rowIndex) => (
                <div className='asset-bulk-row' key={row.key}>
                    <input
                        type='text'
                        id={`asset_name_${row.key}`}
                        name={`asset_name_${row.key}`}
                        ref={(element) => { nameInputRefs.current[row.key] = element; }}
                        value={row.name}
                        placeholder={t('asset.general.name_placeholder')}
                        className={`text ${rowErrors[row.key]?.name ? 'is-invalid' : ''}`}
                        autoFocus={rowIndex === 0}
                        onChange={(event) => updateRow(row.key, 'name', event.target.value)}
                        onKeyDown={(event) => handleNameKeyDown(event, rowIndex)}
                        data-1p-ignore='true'
                    />
                    <Dropdown<AssetTypeOption>
                        id={`asset_type_${row.key}`}
                        name={`asset_type_${row.key}`}
                        options={assetTypes}
                        selectedOption={assetTypes.find(assetType => assetType.id === row.asset_type) ?? null}
                        onChange={(selectedId) => updateRow(row.key, 'asset_type', selectedId ? Number(selectedId) : null)}
                        disabled_selected='asset.general.type_disabled_selected'
                        selectionFormat={(option) => option.name}
                        optionFormat='name'
                        showSearch={false}
                        allowNoneOption={true}
                        isInvalid={Boolean(rowErrors[row.key]?.asset_type)}
                    />
                    <input
                        type='text'
                        id={`asset_number_${row.key}`}
                        name={`asset_number_${row.key}`}
                        value={row.number}
                        placeholder={t('asset.general.number_placeholder')}
                        className='text'
                        onChange={(event) => updateRow(row.key, 'number', event.target.value)}
                        data-1p-ignore='true'
                    />
                    <div className='delete-icon tooltip-icon' onClick={() => removeRow(row.key)}>
                        <FontAwesomeIcon icon={faTrash} />
                    </div>
                </div>
            ))}

            {/* Add another row */}
            <div className='add-new-button' onClick={(event) => { event.preventDefault(); addRow(); }}>
                {t('asset.general.add_row_label')}
            </div>

            <div className='button-row'>
                <div className='buttons-left'>
                    <SecondaryButton
                        onClick={onSwitchToSingle}
                        label='asset.general.add_single_button_label'
                        size='small'
                    />
                </div>
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={handleClose}
                        label='general.cancel'
                        size='small'
                    />
                    <PrimaryButton
                        type='submit'
                        label='asset.general.bulk_save_label'
                        size='small'
                        onlyViewRestriction={true}
                        loading={buttonLoader}
                    />
                </div>
            </div>
        </form>
    );
};

export default AssetBulkForm;
